import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { IasLoaderComponent } from "../../common/ias-loader/ias-loader.component";
import { environment } from '../../../environments/environment';
import { ToastService } from '../../common/toast.service';
import { Meta, Title } from '@angular/platform-browser';

declare var bootstrap: any;

@Component({
  selector: 'app-ias-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HttpClientModule,
    IasLoaderComponent
  ],
  templateUrl: './ias-login.component.html',
  styleUrls: ['./ias-login.component.scss'],
})
export class IasLoginComponent {

  showPassword = false;
  isMobileLogin = false;
  emailForm: FormGroup;
  otpForm: FormGroup;
  loading = false;
  errorMessage = '';
  showOtpStep = false;

  otpControls = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'];
  countdown!: number;
  intervalId: any;
  isResendDisabled = false;

  tempToken: string = '';
  toastMessage = '';
  toastType: string = 'primary';

  activeTab: 'email' | 'mobile' = 'email';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private title: Title,
    private meta: Meta
  ) {

    this.emailForm = this.fb.group({
      email: ['', [Validators.required, this.emailOrMobileValidator]],
      password: ['', Validators.required],
    });

    this.otpForm = this.fb.group({});
    this.otpControls.forEach(ctrl =>
      this.otpForm.addControl(ctrl, new FormControl('', [Validators.required, Validators.pattern('[0-9]')]))
    );
  }

  ngOnInit(): void {
    this.title.setTitle('IASCA Portal - Login');

    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      const refreshToken = params['refreshToken'];
      const error = params['error'];
      const intent = params['intent'];

      if (token && refreshToken) {
        this.handleSsoCallback(token, refreshToken, intent);
      } else if (error) {
        this.showToast(decodeURIComponent(error), 'danger');
      }
    });
  }

  // ✅ FIXED VALIDATOR
  emailOrMobileValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.trim();
    if (!value) return { required: true };

    if (this.isMobileLogin) {
      return /^[6-9]\d{9}$/.test(value) ? null : { invalidInput: true };
    } else {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? null : { invalidInput: true };
    }
  };

  // ✅ TAB SWITCH
  switchTab(tab: 'email' | 'mobile') {
    this.activeTab = tab;
    this.emailForm.reset();

    if (tab === 'mobile') {
      this.isMobileLogin = true;
      this.password?.clearValidators();
      this.password?.setValue('');
    } else {
      this.isMobileLogin = false;
      this.password?.setValidators([Validators.required]);
    }

    this.email?.updateValueAndValidity();
    this.password?.updateValueAndValidity();
  }

  // ✅ MOBILE INPUT CONTROL
  onInputChange(event: any) {
    let value = event.target.value;

    if (this.activeTab === 'mobile') {
      value = value.replace(/[^0-9]/g, '').slice(0, 10);
      this.email?.setValue(value, { emitEvent: false });
    }
  }

  loginWithGoogle() {
    window.location.href = `${environment.apiUrl}/accounts/google/login?intent=login`;
  }

  get email() { return this.emailForm.get('email'); }
  get password() { return this.emailForm.get('password'); }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const inputValue = this.email?.value?.trim();
    let payload: any = {};

    if (this.isMobileLogin) {
      payload.mobile = inputValue;
    } else {
      payload.email = inputValue;
      payload.password = this.password?.value;
    }

    this.http.post(`${environment.apiUrl}/accounts/login`, payload)
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          const token = res?.data?.token;

          if (token) {
            this.tempToken = token;
            this.showOtpStep = true;
            this.startCountdown(59);
            this.showToast('✅ Login successful, please verify OTP.', 'success');
          }
        },
        error: (err) => {
          this.loading = false;
          this.showToast(err.error?.message || '❌ Invalid credentials.', 'danger');
        },
      });
  }

  // ✅ OTP INPUT HANDLING
  handleKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Backspace' && !input.value && index > 0) {
        const prev = input.parentElement?.children[index - 1] as HTMLInputElement;
        prev?.focus();
      }
      return;
    }

    if (!/^[0-9]$/.test(event.key)) event.preventDefault();
  }

  onOtpInput(event: any, index: number) {
    const input = event.target;
    if (input.value && index < this.otpControls.length - 1) {
      const next = document.querySelectorAll<HTMLInputElement>('.otp-inputs input')[index + 1];
      next?.focus();
    }
  }

  submitOtp() {
    if (this.otpForm.valid) {
      this.loading = true;

      const otpValue = this.otpControls.map(ctrl => this.otpForm.get(ctrl)?.value).join('');

      const otpPayload = {
        otp: otpValue,
        action: 'login',
        token: this.tempToken
      };

      this.http.patch(`${environment.apiUrl}/accounts/otpverify`, otpPayload)
        .subscribe({
          next: (res: any) => {
            this.loading = false;

            const { sessionToken, userDetails } = res?.data || {};
            const { email, role, status } = userDetails || {};

            if (sessionToken) {
              localStorage.setItem('authToken', res.data.sessionToken);
              localStorage.setItem('refreshToken', res.data.refreshToken);
              localStorage.setItem('expireAt', res.data.expireAt);
              localStorage.setItem('isLoggedIn', 'true');

              if (email) localStorage.setItem('userEmail', btoa(email));
              if (role) localStorage.setItem('userRole', btoa(role));
              if (status) localStorage.setItem('userStatus', btoa(status));

              this.toastService.showToast('Welcome to IASCA portal', 'success');

              this.router.navigate(['homepage']);
            }
          },
          error: (err) => {
            this.loading = false;
            this.showToast(err.error?.message || '❌ Invalid OTP.', 'danger');
          }
        });
    } else {
      this.otpForm.markAllAsTouched();
    }
  }

  startCountdown(seconds: number) {
    this.countdown = seconds;
    this.isResendDisabled = true;

    clearInterval(this.intervalId);

    this.intervalId = setInterval(() => {
      if (this.countdown > 0) this.countdown--;
      else {
        this.isResendDisabled = false;
        clearInterval(this.intervalId);
      }
    }, 1000);
  }

  onResendOtp() {
    if (this.isResendDisabled) return;
    this.onSubmit();
  }

handleSsoCallback(sessionToken: string, refreshToken: string, intent?: string) {
  this.loading = true;

  const headers = new HttpHeaders({
    Authorization: `Bearer ${sessionToken}`
  });

  this.http.get(`${environment.apiUrl}/accounts/whoami`, { headers }).subscribe({
    next: (res: any) => {
      this.loading = false;

      const user = res?.data;

      if (!user) {
        this.showToast('Invalid user data', 'danger');
        return;
      }

      // ✅ Store tokens
      localStorage.setItem('authToken', sessionToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('isLoggedIn', 'true');

      // ✅ Store SAME things like OTP flow
      if (user.email) localStorage.setItem('userEmail', btoa(user.email));
      if (user.role) localStorage.setItem('userRole', btoa(user.role));
      if (user.status) localStorage.setItem('userStatus', btoa(user.status));

      // ✅ Success flow
      this.toastService.showToast('Welcome to IASCA portal', 'success');

      if (intent) {
        this.router.navigate(['homepage']);
      } else {
        this.router.navigate([intent]);
      }
    },

    error: (err) => {
      this.loading = false;
      console.error(err);
      this.showToast('SSO Login Failed', 'danger');
    }
  });
}

  showToast(message: string, type: string = 'primary') {
    this.toastMessage = message;
    this.toastType = type;

    const toastEl = document.getElementById('liveToast');
    if (toastEl) new bootstrap.Toast(toastEl, { delay: 3000 }).show();
  }
}